import { useState } from 'react'
import { ArrowUpDown } from 'lucide-react'
import { clsx } from 'clsx'
import { useAdminFeedback } from '@/hooks/useAdminFeedback'
import FeedbackTable from './FeedbackTable'
import Pagination from '@/components/admin/shared/Pagination'
import LoadingSpinner from '@/components/admin/shared/LoadingSpinner'
import EmptyState from '@/components/admin/shared/EmptyState'

const PAGE_SIZE = 15

export default function FeedbackPage() {
  const [page, setPage] = useState(1)
  const [feedbackType, setFeedbackType] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')

  const { data, isLoading, error } = useAdminFeedback(page, PAGE_SIZE, feedbackType, sortOrder)

  const handleTypeChange = (type: string | undefined) => {
    setFeedbackType(type)
    setPage(1)
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { label: 'All',      value: undefined    },
            { label: 'Positive', value: 'positive'   },
            { label: 'Negative', value: 'negative'   },
          ].map((opt) => (
            <button
              key={opt.label}
              onClick={() => handleTypeChange(opt.value)}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                feedbackType === opt.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md"
        >
          <ArrowUpDown className="w-3 h-3" />
          {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
        </button>

        {data && (
          <span className="text-xs text-gray-400 ml-auto">{data.total} total entries</span>
        )}
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <p className="text-red-500 text-sm py-8 text-center">Failed to load feedback.</p>
      ) : !data || data.items.length === 0 ? (
        <EmptyState message="No feedback entries found" />
      ) : (
        <>
          <FeedbackTable items={data.items} />
          <Pagination page={page} totalPages={data.total_pages} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
